import { actions, inputs, overlays } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { MAX_GROUP_NAME_LENGTH } from './constants';
import { checkForHTMLTags, sanitizeInput } from '../../common/util';

import './createGroupModal.css';
import { LeaderboardInfo } from './data';
import { groupBy } from '../../tools/object';
import { LeaderboardType, ProductGroup, GroupType } from './types';
import { getPluralisedLeaderboardType } from './helper';

const { Modal } = overlays;
const { Button } = actions;
const { TextInput, RadioButtons, CheckboxGroup, Checkbox, CheckboxState } = inputs;

type CheckboxValue = {
  name: string;
  id: string;
  state: string;
  groupType: GroupType;
  modelIds: string[];
};

enum RadioOptions {
  NEW = 'new',
  EXISTING = 'existing',
}
export interface CreateGroupModalProps {
  onClose: () => void;
  isOpen: boolean;
  onCreateGroup: (names?: string[]) => void;
  title: string;
  isLoading?: boolean;
  selected: string[];
  leaderboardData: LeaderboardInfo;
  createGroups: ({ leaderboardData }?: { leaderboardData?: LeaderboardInfo }) => ProductGroup[];
  leaderboardType: LeaderboardType;
}

const radioOptions = [
  {
    value: RadioOptions.NEW,
    label: 'New group',
  },
  {
    value: RadioOptions.EXISTING,
    label: 'Existing group',
  },
];

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  onClose,
  isOpen,
  onCreateGroup,
  title,
  isLoading,
  selected,
  leaderboardData,
  createGroups,
  leaderboardType,
}) => {
  const [groups, setGroups] = useState<CheckboxValue[]>([]);
  const [groupName, setGroupName] = useState<string>();
  const [GroupNameError, setGroupNameError] = useState('');
  const [selectedOption, setSelectedOption] = useState<string>(RadioOptions.NEW);

  const handleSelectChange = useCallback((value: CheckboxValue[]) => {
    setGroups(prevGroups => updateGroups(prevGroups, value));
  }, []);

  const handleOptionChange = value => {
    setSelectedOption(value);
  };

  useEffect(() => {
    if (!isOpen) return;
    const productGroups = createGroups({ leaderboardData });
    setGroupName('');
    setGroupNameError('');
    setSelectedOption(RadioOptions.NEW);
    setGroups(
      productGroups.map(pg => ({
        ...pg,
        state: selected.every(select => pg.modelIds.includes(select)) ? CheckboxState.CHECKED : CheckboxState.UNCHECKED,
      }))
    );
  }, [leaderboardData, isOpen, selected, createGroups]);

  const isGroupNew = selectedOption === RadioOptions.NEW;
  const isGroupExisting = selectedOption === RadioOptions.EXISTING;

  const primaryButtonText = isGroupNew ? 'Create group' : 'Add to selected group';

  const handleCreateGroup = () => {
    if (isGroupNew) {
      onCreateGroup([groupName.trim()]);
    } else {
      onCreateGroup(groups.filter(group => group.state === CheckboxState.CHECKED).map(group => group.id));
    }
    handleClose();
  };

  const groupNameExists = groups?.some(group => group.name.trim() === groupName.trim());

  const isGroupNameEmpty = isGroupNew && !groupName;
  const isAnyGroupChecked = groups.some(
    group => group.state === CheckboxState.CHECKED && !selected.every(select => group.modelIds.includes(select))
  );
  const isGroupNameDuplicate = isGroupNew && groupNameExists;

  const primaryButtonDisabled =
    isGroupNameEmpty || (!isGroupNew && !isAnyGroupChecked) || isGroupNameDuplicate || !!GroupNameError;

  const handleClose = () => {
    setGroupName('');
    setSelectedOption(RadioOptions.NEW);
    onClose();
  };

  const groupsByGroupType = groupBy(groups, group => group.groupType);

  const handleInputChange = (input: string): void => {
    const sanitizedInput = sanitizeInput(input);
    const error = checkForHTMLTags(sanitizedInput);
    if (error) {
      setGroupNameError(error);
    } else {
      setGroupNameError('');
    }
    setGroupName(sanitizedInput);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        <RadioButtons value={selectedOption} options={radioOptions} onChange={handleOptionChange} />
        <div className="mt-4">
          {isGroupNew && (
            <TextInput
              name="new-group"
              label="Group name"
              placeholder=""
              value={groupName}
              maxLength={MAX_GROUP_NAME_LENGTH}
              onChange={handleInputChange}
              errorMessage={groupNameExists ? 'Group already exists, try another name' : GroupNameError}
              hasError={groupNameExists || !!GroupNameError}
            />
          )}
          {isGroupExisting && (
            <ProductGroups
              groupsByGroupType={groupsByGroupType}
              handleSelectChange={handleSelectChange}
              selected={selected}
              leaderboardType={leaderboardType}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} mode="secondary">
          Cancel
        </Button>
        <Button onClick={handleCreateGroup} mode="primary" disabled={primaryButtonDisabled} isLoading={isLoading}>
          {primaryButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

function ProductGroups({ groupsByGroupType, handleSelectChange, selected, leaderboardType }) {
  const groupsByGroupTypeEntries = Object.entries(groupsByGroupType);
  return groupsByGroupTypeEntries.length > 0 ? (
    <>
      {groupsByGroupTypeEntries
        .sort(([groupTypeName]) => (groupTypeName === GroupType.selectGroups ? -1 : 1)) //"Select groups" at the top
        .map(([groupTypeName, groups]: [GroupType, CheckboxValue[]]) => (
          <div key={groupTypeName} className="groupListPLB">
            <CheckboxGroup title={groupTypeName} onChange={handleSelectChange}>
              {groupTypeName === GroupType.selectGroups && <GroupDisclaimer leaderboardType={leaderboardType} />}
              {groups.map(group => {
                const rowSelected = selected.every(select => group.modelIds.includes(select));
                return (
                  <Checkbox
                    id={group.id}
                    key={group.id}
                    value={group.id}
                    name={group.id}
                    state={group.state === CheckboxState.CHECKED ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                    label={group.id}
                    disabled={rowSelected}
                  />
                );
              })}
            </CheckboxGroup>
          </div>
        ))}
    </>
  ) : (
    <NoGroupMessage leaderboardType={leaderboardType} />
  );
}

function GroupDisclaimer({ leaderboardType }) {
  return (
    <span className="selectGroupInfoPLB">
      {getPluralisedLeaderboardType(leaderboardType)} that are already in those folders will not be added as duplicates
    </span>
  );
}

function NoGroupMessage({ leaderboardType }) {
  return (
    <>
      <p className="font-bold text-md">You do not have any groups now</p>
      <p className="text-md">
        Create at least one so that you can add more {getPluralisedLeaderboardType(leaderboardType)} to it in the
        future.
      </p>
    </>
  );
}

function updateGroups(groups: CheckboxValue[], values: CheckboxValue[]): CheckboxValue[] {
  return groups.map(group => {
    const updated = values.find(val => val.id === group.id);
    return updated ? { ...group, ...updated } : group;
  });
}
